import React, { useEffect, useState } from "react";
import {
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
  Button,
  Box,
  Modal,
  TextField,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  Backdrop,
  LinearProgress
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import { connect } from "react-redux";
import Pagination from "@mui/material/Pagination";
import { requestState } from "../../../redux/type/type";
import { getAllImages } from "../../../redux/gallery/gallerySelector";
import { fetchAllImages, uploadImage, deleteImage, updateImage, showAlert } from "../../../redux";

const limit = 12;

const Gallery = (props) => {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageData, setImageData] = useState({
    title: "",
    height: 500,
    width: 500,
    media: null,
  });
  const [editData, setEditData] = useState({
    title: "",
    height: 500,
    width: 500,
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  const { images, fetchAllImages, uploadImage, deleteImage, updateImage } = props;
  const image_count = images.images.count ? parseInt(images.images.count) : 0;
  const page_count = Math.ceil(image_count / limit);
  const mediaBaseURL = process.env.REACT_APP_MEDIA_BASE_URL;

  useEffect(() => {
    fetchAllImages({ limit: limit, offset: 0 });
  }, []);

  const handleChange = (e, p) => {
    setPage(p);
    fetchAllImages({ limit: limit, offset: (p - 1) * limit });
  };

  const handleAddImageModel = () => {
    setOpen(true);
  };

  const handleAddImageModelClose = () => {
    setOpen(false);
    setImageData({
      title: "",
      height: 500,
      width: 500,
      media: null,
    });
    setPreviewUrl("");
    setUploadProgress(0);
  };

  const addImageHandler = async (e) => {
    e.preventDefault();
    
    if (!imageData.media) {
      props.showAlert("Please select an image file", "error");
      return;
    }

    const formData = new FormData();
    formData.append("title", imageData.title);
    formData.append("height", imageData.height);
    formData.append("width", imageData.width);
    formData.append("media", imageData.media);

    try {
      await uploadImage(formData).unwrap();
      handleAddImageModelClose();
      // Refresh the gallery
      fetchAllImages({ limit: limit, offset: (page - 1) * limit });
      props.showAlert("Image uploaded successfully!", "success");
    } catch (rejectedValueOrSerializedError) {
      props.showAlert("Image upload failed!", "error");
    }
  };

  const setImageValue = (e) => {
    setImageData({ ...imageData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageData({ ...imageData, media: file });
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewUrl(event.target.result);
      };
      reader.readAsDataURL(file);

      // Auto-fill title from filename
      if (!imageData.title) {
        const fileName = file.name.split('.')[0];
        setImageData(prev => ({ ...prev, title: fileName, media: file }));
      }
    }
  };

  const handleDeleteClick = (imageId) => {
    setSelectedImageId(imageId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteImage(selectedImageId).unwrap();
      setDeleteConfirmOpen(false);
      setSelectedImageId(null);
      props.showAlert("Image deleted successfully!", "success");
    } catch (error) {
      props.showAlert("Failed to delete image!", "error");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setSelectedImageId(null);
  };

  const handleEditClick = (image) => {
    setSelectedImage(image);
    setEditData({
      title: image.title,
      height: image.height,
      width: image.width,
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedImage(null);
    setEditData({
      title: "",
      height: 500,
      width: 500,
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await props.updateImage({
        imageId: selectedImage.id,
        data: editData
      }).unwrap();
      
      handleEditClose();
      // Refresh the gallery
      fetchAllImages({ limit: limit, offset: (page - 1) * limit });
      props.showAlert("Image updated successfully!", "success");
    } catch (error) {
      props.showAlert("Failed to update image!", "error");
    }
  };

  const setEditValue = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <Grid container spacing={3} justifyContent="center">
        {/* Header */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" gutterBottom>
                Gallery
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your image collection
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleAddImageModel}
              size="large"
            >
              Upload Image
            </Button>
          </Box>
        </Grid>

        {/* Stats */}
        <Grid item xs={12}>
          <Box display="flex" gap={2} sx={{ mb: 2 }}>
            <Chip
              icon={<ImageIcon />}
              label={`${image_count} Images`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Grid>

        {/* Image Grid */}
        <Grid item xs={12}>
          {images.images.status === requestState.loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <ImageList cols={4} gap={16} sx={{ width: "100%", margin: 0 }}>
              {images.images.results && images.images.results.length > 0
                ? images.images.results.map((item) => (
                    <ImageListItem key={item.id} sx={{ overflow: 'hidden' }}>
                      <Card 
                        sx={{ 
                          position: 'relative', 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={`${mediaBaseURL}${item.media_url}`}
                          alt={item.title}
                          sx={{ 
                            objectFit: 'cover',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                          onClick={() => props?.handleClick && props.handleClick(item.id)}
                        />
                        <Box sx={{ p: 2, flexGrow: 1 }}>
                          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {item.width} × {item.height}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <CardActions
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(0,0,0,0.6)',
                            borderRadius: 1,
                            minHeight: 'auto',
                            padding: '4px',
                            display: 'flex',
                            gap: '4px'
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(item);
                            }}
                            sx={{ color: 'white', padding: '4px' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(item.id);
                            }}
                            sx={{ color: 'white', padding: '4px' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </ImageListItem>
                  ))
                : (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={8}
                      sx={{ gridColumn: '1 / -1' }}
                    >
                      <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No images found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Upload your first image to get started
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleAddImageModel}
                      >
                        Upload Image
                      </Button>
                    </Box>
                  )}
            </ImageList>
          )}
        </Grid>

        {/* Pagination */}
        {page_count > 1 && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={page_count}
                variant="outlined"
                onChange={handleChange}
                page={page}
                color="primary"
                size="large"
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Upload Modal */}
      <Modal
        open={open}
        onClose={handleAddImageModelClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" component="h2">
                Upload New Image
              </Typography>
              <IconButton onClick={handleAddImageModelClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box component="form" onSubmit={addImageHandler}>
              {/* Image Preview */}
              {previewUrl && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 8,
                      border: '1px solid #ddd'
                    }}
                  />
                </Box>
              )}

              {/* File Upload */}
              <Box sx={{ mb: 3 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{ py: 2 }}
                  >
                    {imageData.media ? imageData.media.name : 'Choose Image File'}
                  </Button>
                </label>
                {imageData.media && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Size: {formatFileSize(imageData.media.size)}
                  </Typography>
                )}
              </Box>

              {/* Form Fields */}
              <TextField
                fullWidth
                label="Image Title"
                name="title"
                value={imageData.title}
                onChange={setImageValue}
                sx={{ mb: 2 }}
                required
              />

              <Box display="flex" gap={2} sx={{ mb: 3 }}>
                <TextField
                  label="Width (px)"
                  name="width"
                  type="number"
                  value={imageData.width}
                  onChange={setImageValue}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Height (px)"
                  name="height"
                  type="number"
                  value={imageData.height}
                  onChange={setImageValue}
                  sx={{ flex: 1 }}
                />
              </Box>

              {/* Upload Progress */}
              {props.image?.status === requestState.loading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                    Uploading image...
                  </Typography>
                </Box>
              )}

              {/* Actions */}
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={handleAddImageModelClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!imageData.media || props.image?.status === requestState.loading}
                >
                  Upload Image
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={deleteConfirmOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              Delete Image
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete this image? This action cannot be undone.
            </Typography>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={handleDeleteCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteConfirm}
                disabled={props.image?.status === requestState.loading}
              >
                {props.image?.status === requestState.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  'Delete'
                )}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Edit Image Modal */}
      <Modal
        open={editOpen}
        onClose={handleEditClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={editOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" component="h2">
                Edit Image Details
              </Typography>
              <IconButton onClick={handleEditClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box component="form" onSubmit={handleEditSubmit}>
              {/* Image Preview */}
              {selectedImage && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <img
                    src={`${mediaBaseURL}${selectedImage.media_url}`}
                    alt={selectedImage.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 8,
                      border: '1px solid #ddd'
                    }}
                  />
                </Box>
              )}

              {/* Form Fields */}
              <TextField
                fullWidth
                label="Image Title"
                name="title"
                value={editData.title}
                onChange={setEditValue}
                sx={{ mb: 2 }}
                required
              />

              <Box display="flex" gap={2} sx={{ mb: 3 }}>
                <TextField
                  label="Width (px)"
                  name="width"
                  type="number"
                  value={editData.width}
                  onChange={setEditValue}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Height (px)"
                  name="height"
                  type="number"
                  value={editData.height}
                  onChange={setEditValue}
                  sx={{ flex: 1 }}
                />
              </Box>

              {/* Actions */}
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={handleEditClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={props.image?.status === requestState.loading}
                >
                  {props.image?.status === requestState.loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Update Image'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    images: getAllImages(state),
    image: state.galleryState?.image || { status: requestState.idle },
    alert: state.alert
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchAllImages: (data) => dispatch(fetchAllImages(data)),
    uploadImage: (data) => dispatch(uploadImage(data)),
    deleteImage: (id) => dispatch(deleteImage(id)),
    updateImage: (data) => dispatch(updateImage(data)),
    showAlert: (message, type) => dispatch(showAlert(message, type)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Gallery);
